import { Schema } from 'effect';
import { Price, Uuid } from './shared';

/**
 * What every calculation carries, whatever became of it.
 *
 * Not exported: nothing takes a calculation that has not said how it went. Callers take {@link ICalculation} and tell the four apart by `status`, or by the classes themselves.
 *
 * The engine answers with a great deal more — the whole configuration back, the environment it ran in, who asked and when, and some thirty measurements around the one price. Only what the Configurator acts on is declared, the way `Account` declares only what it uses, so a change to a field nobody reads cannot fail a
 * call.
 */
class Calculation extends Schema.Class<Calculation>('Calculation')(
	Schema.Struct({
		id: Uuid,
	}),
) {}

/**
 * What a part of the window costs to make, split the way the engine costs it.
 *
 * The two halves rather than the sum, because that is what the engine answers with, and a schema that added them up would be deciding what a caller is shown. {@link Bom} does the adding, in one place, for the callers that want the one number.
 */
const CostPrice = Schema.Struct({
	materials: Price,
	wages: Price,
});

/**
 * Something the engine measured: a number, and never less than nothing.
 *
 * The same rule as {@link Price} and not the same thing, so neither can be widened in the other's name: a weight is not an amount of money, and the day one of the two may be negative the other must not follow.
 *
 * Finite for the reason `Price` is: NaN passes a greater-than check by failing it in both directions, and an infinity passes it outright. Either would reach the panel and be shown as a measurement.
 */
const Measurement = Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0));

/**
 * Something the engine measured that may fall either side of nothing.
 *
 * Apart from {@link Measurement} because the two say different things, and what they say is the point: a weight below nothing is a wrong answer, and a reference window below nothing is a window that loses more heat than it gains — which most windows do. A schema that refused it would refuse the ordinary case.
 */
const SignedMeasurement = Schema.Finite;

/**
 * What the engine worked out about the position it priced.
 *
 * Named rather than declared inline on {@link CalculationSucceeded}, because two other places speak about it: a priced position carries one, and the properties panel shows one. A type nobody can name is a type every caller restates.
 *
 * A class rather than a struct, so the three costs the engine reports in halves can be read as one number without every caller adding them up the same way — the way `CalculationSucceeded` reads its price out of this.
 *
 * The engine answers with some thirty measurements around the price; only what the Configurator shows is declared, for the reason the {@link Calculation} doc gives.
 */
export class Bom extends Schema.Class<Bom>('Bom')(
	Schema.Struct({
		costPrice: Price,
		costPriceCoating: CostPrice,
		costPriceElements: CostPrice,
		costPriceGlasses: CostPrice,
		eref: SignedMeasurement,
		glassWeight: Measurement,
		listPrice: Price,
		listPriceCoating: Price,
		listPriceElements: Price,
		listPriceGlasses: Price,
		soundRW: Measurement,
		sunlightTransmittance: Measurement,
		uValue: SignedMeasurement,
		weight: Measurement,
	}),
) {
	/** What the elements cost to make altogether. */
	public get costPriceElementsTotal(): number {
		return this.costPriceElements.wages + this.costPriceElements.materials;
	}

	/** What the glass costs to make altogether. */
	public get costPriceGlassesTotal(): number {
		return this.costPriceGlasses.wages + this.costPriceGlasses.materials;
	}

	/** What the coating costs to make altogether. */
	public get costPriceCoatingTotal(): number {
		return this.costPriceCoating.wages + this.costPriceCoating.materials;
	}
}

/** What the engine worked out about the position it priced. */
export type IBom = typeof Bom.Type;

/** A calculation the engine has taken on and has not started on. */
export class CalculationPending extends Calculation.extend<CalculationPending>('CalculationPending')(
	Schema.Struct({
		status: Schema.Literal('pending'),
	}),
) {}

/**
 * A calculation the engine has started and is still working out.
 *
 * Distinct from `pending` on the wire, and the same thing to everyone here: neither has a price, and both mean asking again. It is declared all the same, because a status nothing has a name for is a status that fails to decode.
 */
export class CalculationProcessing extends Calculation.extend<CalculationProcessing>('CalculationProcessing')(
	Schema.Struct({
		status: Schema.Literal('processing'),
	}),
) {}

/**
 * A calculation the engine finished, which is the only kind that has a price.
 *
 * `bom` is required here rather than optional on a single shared class, which is the whole point of telling the four apart: a succeeded calculation always priced something, so no caller has to check for a price the status has already promised.
 */
export class CalculationSucceeded extends Calculation.extend<CalculationSucceeded>('CalculationSucceeded')(
	Schema.Struct({
		bom: Bom,
		status: Schema.Literal('succeeded'),
	}),
) {
	/** What the position costs. */
	public get listPrice(): number {
		return this.bom.listPrice;
	}
}

/** A calculation the engine gave up on, and what it said about why. */
export class CalculationFailed extends Calculation.extend<CalculationFailed>('CalculationFailed')(
	Schema.Struct({
		error: Schema.NonEmptyString,
		status: Schema.Literal('failed'),
	}),
) {}

/**
 * A calculation in whichever of its four states, as the engine reports it.
 *
 * Exported as the schema and not only as the type, because the call that reads a calculation off the wire decodes with it.
 */
export const Calculations = Schema.Union([CalculationPending, CalculationProcessing, CalculationSucceeded, CalculationFailed]);

/** A calculation in whichever of its four states the engine reports. */
export type ICalculation = typeof Calculations.Type;
